/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import i18n from 'i18next';

// Component imports
import { Button, Card, Col, Collapse, Row, Select, Space, Switch, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import FormSectionHeader from '../UI/FormSectionHeader';

// Misc imports
import { LANGUAGE_OPTIONS } from '../../constants/main';
import { useFetchNlpPipelinesQuery } from '../../store/nlpPipelines/nlpPipelinesApi';
import { useClearCacheMutation, useUpdateBotMutation } from '../../store/bots/botsApi';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { POWERUPS } from '../../constants/main';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { useFetchNlpFlowsQuery } from '../../store/nlpFlows/nlpFlowsApi';
import is from '../../utils/is';
import useTheme from '../../utils/hooks/useTheme';
import EntryPointSelect from './Fields/EntryPointSelect';

const generateMappingOptions = (nlpPipelines, nlpFlows, languageCode) => {
  let recommendations = [];

  if (is.array(nlpPipelines) && !is.emptyArray(nlpPipelines)) {
    recommendations = [
      ...recommendations,
      {
        label: i18n.t('NLP Systems'),
        options: nlpPipelines
          .filter(nlpPipeline => nlpPipeline.language === languageCode || nlpPipeline?.secondaryLanguages?.includes(languageCode))
          .map(nlpPipeline => {
            return {
              value: nlpPipeline.id,
              name: nlpPipeline.name,
              label: (
                <Space size={4}>
                  <Typography.Text>
                    <Space size={8}>
                      {nlpPipeline.name}
                    </Space>
                  </Typography.Text>
                </Space>
              )
            };
          })
      }
    ];
  }

  if (is.array(nlpFlows) && !is.emptyArray(nlpFlows)) {
    recommendations = [
      ...recommendations,
      {
        label: i18n.t('NLP Flows'),
        options: nlpFlows.map(nlpFlow => {
          return {
            value: nlpFlow.id,
            name: nlpFlow.name,
            label: (
              <Space size={4}>
                <Typography.Text>
                  <Space size={8}>
                    {nlpFlow.name}
                  </Space>
                </Typography.Text>
              </Space>
            )
          };
        })
      }
    ];
  }

  return recommendations;
};

const NlpConfigurationForm = () => {
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const { botLanguages } = useBotLanguage();
  const { formProps } = useTheme();

  // Store state
  const { data: bot, isFetching: isFetchingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const { data: nlpPipelines, isLoading: isLoadingNlpPipelines, isFetching: isFetchingNlpPipelines } = useFetchNlpPipelinesQuery({ orgId, tenantId });
  const { data: nlpFlows, isLoading: isLoadingNlpFlows } = useFetchNlpFlowsQuery({ orgId, tenantId });

  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation();
  const [clearCache, { isLoading: isClearingCache }] = useClearCacheMutation();

  return (
    <Card>
      <Collapse defaultActiveKey={['1']} ghost>
        <Collapse.Panel
          showArrow={false}
          header={<FormSectionHeader title={t('LANGUAGE - NLP MAPPING')} />}
          key="1"
        >
          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text
                className="fs-base"
              >
                {t('Language Mapping')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                {t('Map each Agent Language directly to one of the available NLP Systems or dynamically select preferable NLP Systems or different prompts through a flow with variables.')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              {
                LANGUAGE_OPTIONS.filter(opt => botLanguages.includes(opt.key)).map(lang => {
                  return (
                    <Row>
                      <Col xs={24} sm={6} md={4} lg={3}>
                        {lang.label}:
                      </Col>

                      <Col xs={24} sm={18} md={10} lg={8}>
                        <Select
                          // We need to take into account both nlpMap (for pipelines) and nlpTreeMap (for flows)
                          value={(!isLoadingNlpPipelines && !isLoadingNlpFlows) ? (bot.nlpMap?.[lang.key]?.id || bot.nlpTreeMap?.[lang.key]?.id || null) : null}
                          placeholder={t('Choose NLP System')}
                          className="mb-4 w-100"
                          allowClear
                          dropdownMatchSelectWidth={false}
                          loading={isFetchingNlpPipelines || isUpdatingBot || isFetchingBot}
                          disabled={isFetchingNlpPipelines || isUpdatingBot || isFetchingBot}
                          onChange={pipelineId => {
                            const currentNlpMapRef = {
                              // Convert nlpMap to key/value pair with the pipeline ID as the value
                              ...Object.fromEntries(Object.entries(bot.nlpMap || {}).map(([key, value]) => ([key, value.id]))),
                              // Convert nlpTreeMap to key/value pair with the pipeline ID as the value
                              ...Object.fromEntries(Object.entries(bot.nlpTreeMap || {}).map(([key, value]) => ([key, value.id])))
                            };

                            if (pipelineId === null) {
                              // Remove the mapping for the language
                              delete currentNlpMapRef[lang.key];
                            } else {
                              // Set the new mapping
                              currentNlpMapRef[lang.key] = pipelineId;
                            }

                            updateBot({ orgId, tenantId, data: { nlpMapRef: currentNlpMapRef } });
                          }}
                          options={generateMappingOptions(nlpPipelines?.items, nlpFlows?.items, lang.key)}
                        />
                      </Col>
                    </Row>
                  );
                })
              }
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <Collapse defaultActiveKey={['1']} ghost>
        <Collapse.Panel
          showArrow={false}
          header={<FormSectionHeader title={t('SETTINGS')} />}
          key="1"
        >
          <Row gutter={formProps.rowGutter} className={formProps.flexDirection}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text
                className="fs-base"
              >
                {t('Optimization Settings')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">

                {t('Configure the agent\'s NLP settings, like enabling auto-train or caching.')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Row>
                <Space align="start">
                  <Typography.Paragraph className="mb-2" style={{ minWidth: 100 }}>{t('Auto Train')}</Typography.Paragraph>
                  <Switch
                    className="ml-3"
                    onChange={async (value) => {
                      updateBot({ orgId, tenantId, data: { settings: { ...bot?.settings, autoTrain: value } } });
                    }}
                    defaultChecked={bot?.settings?.autoTrain ?? false}
                  />
                </Space>
              </Row>

              {
                bot.powerups.includes(POWERUPS.NLP_CACHING.key) ? (
                  <Fragment>
                    <Row>
                      <Space align="start">
                        <Typography.Paragraph className="mb-2" style={{ minWidth: 100 }}>{t('Enable Caching')}</Typography.Paragraph>
                        <Switch
                          className="ml-3"
                          onChange={async (value) => {
                            updateBot({ orgId, tenantId, data: { nluCachingEnabled: value } });
                          }}
                          defaultChecked={bot?.nluCachingEnabled ?? false}
                        />
                      </Space>
                    </Row>

                    {
                      bot?.nluCachingEnabled ? (
                        <Space align="baseline" className="mt-5">
                          <Typography.Paragraph className="mb-2">{t('Purge cache')}</Typography.Paragraph>

                          <Button
                            className="ml-3"
                            type="text"
                            onClick={() => clearCache({ orgId, tenantId })}
                            icon={<ReloadOutlined />}
                            loading={isClearingCache}
                          />
                        </Space>
                      ) : null
                    }
                  </Fragment>
                ) : null
              }
            </Col>
          </Row>
          <Row gutter={formProps.rowGutter} className={`${formProps.flexDirection} mt-10`}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text
                className="fs-base"
              >
                {t('NLP Error Response')}
              </Typography.Text>
              <Typography.Paragraph className="fs-sm" type="secondary">
                <Trans t={t}>
                  Select an agent flow to be triggered when NLP processing fails or returns an error. If no flow is set, a fixed system message will be shown: <Typography.Text italic>
                  "I am having trouble processing your request. Please try again in a moment."</Typography.Text></Trans>
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Typography.Paragraph className="mb-2">{t('Agent Flow')}</Typography.Paragraph>
              <EntryPointSelect
                className="w-30"
                allowClear
                value={bot?.settings?.nlp?.fallbackNlpFlowId}
                placeholder={t('Select flow to be triggered')}
                onChange={async (flowId) => {
                  updateBot({ orgId, tenantId, data: { settings: { nlp: { fallbackNlpFlowId: flowId } } } });
                }}
              />
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

    </Card>
  );
};

export default NlpConfigurationForm;
